import type { CSSProperties, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router";
import { BrandMark, focusRing, LogoutIcon, Notice } from "../../ui/index.js";
import { LocaleSwitch } from "../auth/index.js";
import { BackLink } from "./BackLink.js";
import { useLogout } from "./useLogout.js";

export interface ShellNavItem {
  to: string;
  icon: ReactNode;
  label: string;
  /** Подпись в боковой панели, если отличается от вкладки (на телефоне «Ещё», на широком экране «Настройки»). */
  wideLabel?: string;
  badge?: number;
  /** Красный счётчик (непрочитанные), а не янтарный (ждут ответа). */
  badgeTone?: "amber" | "red";
}

/**
 * Оболочка кабинета, общая для владельца и админа. Одна вёрстка:
 * на телефоне шапка с салоном плюс нижние вкладки (уведомления тоже вкладка, «Ещё» крайняя справа);
 * от `lg` (1024px) боковая панель слева (знак продукта и салон, навигация, выход, язык), контент не шире ~1160px и по центру.
 * Иконки пунктов одного набора (`ui/icons.tsx`) на обоих размерах; активный пункт в цвете бренда. Выход на телефоне живёт в «Ещё».
 */
export function CabinetShell({
  salonName,
  items,
  backTo,
  banner,
  children,
}: {
  salonName: string;
  items: ShellNavItem[];
  backTo: string | null;
  banner?: ReactNode;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const { logout, pending: leaving, error: logoutError } = useLogout();
  return (
    <div className="mx-auto flex min-h-dvh w-full max-w-md flex-col bg-page lg:max-w-none lg:flex-row">
      {/* Колонка на всю высоту страницы, содержимое прилипает к верху при прокрутке. */}
      <aside className="hidden lg:block lg:w-64 lg:shrink-0 lg:border-r lg:border-line lg:bg-white">
        <div className="sticky top-0 flex h-dvh flex-col">
          <div className="flex items-center gap-3 px-5 pt-5 pb-4">
            <BrandMark className="h-9 w-9 shrink-0" />
            <div className="min-w-0">
              <div className="text-xs text-ink-3">{t("common.salon")}</div>
              <div className="truncate font-semibold text-ink">{salonName}</div>
            </div>
          </div>
          <nav aria-label={t("nav.main")} className="flex flex-1 flex-col gap-0.5 px-3">
            {items.map((item) => (
              <SideLink key={item.to} item={item} />
            ))}
          </nav>
          <div className="px-3 pb-2">
            {logoutError && (
              <div className="mb-2">
                <Notice kind="error">{t("common.error")}</Notice>
              </div>
            )}
            <button
              type="button"
              onClick={() => void logout()}
              disabled={leaving}
              className={`flex w-full items-center gap-3 rounded-field px-3 py-2.5 text-left text-sm font-medium text-ink-2 transition hover:bg-slate-100 hover:text-ink disabled:opacity-50 ${focusRing}`}
            >
              <LogoutIcon className="h-5 w-5 shrink-0" />
              {t("nav.logout")}
            </button>
          </div>
          <div className="flex items-center justify-between gap-2 border-t border-line px-5 py-4">
            <span className="text-xs text-ink-3">{t("settings.language")}</span>
            <LocaleSwitch />
          </div>
        </div>
      </aside>

      {/* `--shell-banner`: высота плашки над содержимым (кабинет админа); страницы во весь экран вычитают её из высоты. */}
      <div className="flex min-h-dvh min-w-0 flex-1 flex-col" style={banner ? ({ "--shell-banner": "2.25rem" } as CSSProperties) : undefined}>
        <div className="sticky top-0 z-20 lg:static">
          {banner}
          <header className="flex items-center justify-between gap-3 border-b border-line bg-white/95 px-4 py-2.5 backdrop-blur lg:hidden">
            <div className="flex min-w-0 items-center gap-3">
              <BrandMark className="h-8 w-8 shrink-0" />
              <div className="min-w-0">
                {backTo ? <BackLink to={backTo} className="text-sm font-medium text-brand" /> : <div className="text-xs text-ink-3">{t("common.salon")}</div>}
                <div className="truncate font-semibold leading-tight text-ink">{salonName}</div>
              </div>
            </div>
          </header>
        </div>
        <main className="flex-1 px-4 pt-4 pb-24 lg:mx-auto lg:w-full lg:max-w-[1160px] lg:px-8 lg:pt-6 lg:pb-10">
          {backTo && (
            <div className="hidden lg:block">
              <BackLink to={backTo} className="mb-3 text-sm font-medium text-ink-2 hover:text-ink" />
            </div>
          )}
          {children}
        </main>
        <nav
          aria-label={t("nav.main")}
          className="fixed inset-x-0 bottom-0 z-20 mx-auto flex max-w-md border-t border-line bg-white pb-[env(safe-area-inset-bottom)] shadow-[0_-4px_16px_rgba(15,23,42,0.06)] lg:hidden"
        >
          {items.map((item) => (
            <Tab key={item.to} item={item} />
          ))}
        </nav>
      </div>
    </div>
  );
}

/** Счётчик вкладки: янтарный (ждут ответа) с тёмным текстом, красный (непрочитанные) с белым; обе пары не ниже AA. */
const badgeClass = (tone: "amber" | "red" = "amber") =>
  `min-w-5 rounded-full px-1.5 text-center text-xs font-semibold leading-5 ${tone === "red" ? "bg-red-600 text-white" : "bg-amber-400 text-amber-950"}`;

/** Счётчик с подписью для экранного чтеца: число само по себе ничего не значит. */
function Counter({ item }: { item: ShellNavItem }) {
  const { t } = useTranslation();
  const badge = item.badge ?? 0;
  if (badge <= 0) return null;
  return (
    <>
      <span className="sr-only">{item.badgeTone === "red" ? t("nav.unreadCount", { count: badge }) : t("nav.waitingCount", { count: badge })}</span>
      <span aria-hidden="true" className={badgeClass(item.badgeTone)}>
        {badge > 99 ? "99+" : badge}
      </span>
    </>
  );
}

/** Вкладка нижней панели телефона. */
function Tab({ item }: { item: ShellNavItem }) {
  const badge = item.badge ?? 0;
  return (
    <NavLink
      to={item.to}
      className={({ isActive }) =>
        `relative flex min-w-0 flex-1 flex-col items-center justify-center gap-0.5 rounded-lg pt-2 pb-1.5 text-[11px] font-medium ${focusRing} ${isActive ? "text-brand" : "text-ink-3"}`
      }
    >
      {({ isActive }) => (
        <>
          <span className={`flex h-7 w-12 items-center justify-center rounded-full ${isActive ? "bg-brand-soft" : ""}`} aria-hidden="true">
            {item.icon}
          </span>
          <span className="max-w-full truncate tracking-tight">{item.label}</span>
          {badge > 0 && (
            <span className="absolute top-1 left-1/2 ml-2">
              <Counter item={item} />
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}

/** Пункт боковой панели широкого экрана. */
function SideLink({ item }: { item: ShellNavItem }) {
  return (
    <NavLink
      to={item.to}
      className={({ isActive }) =>
        `flex items-center gap-3 rounded-field px-3 py-2.5 text-sm font-medium transition ${focusRing} ${
          isActive ? "bg-brand-soft text-brand" : "text-ink-2 hover:bg-slate-100 hover:text-ink"
        }`
      }
    >
      <span className="flex w-6 shrink-0 justify-center" aria-hidden="true">
        {item.icon}
      </span>
      <span className="flex-1 truncate">{item.wideLabel ?? item.label}</span>
      <Counter item={item} />
    </NavLink>
  );
}
